import React, { useEffect, useState, useCallback } from 'react';
import OnboardingChecklist from '../components/OnboardingChecklist';
import {
  fetchDashboardStats,
  fetchLinkedAccounts,
  fetchConsentLogs,
} from '../services/adminApi';
import { loginUser, registerUser, listApiApps, createApiApp } from '../../services/api';

// ─── Login Screen ──────────────────────────────────────
const LoginScreen = ({ onLogin }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = isRegister
        ? await registerUser(email, password, fullName)
        : await loginUser(email, password);
      if (res.success) {
        localStorage.setItem('afrilink_token', res.data.token);
        localStorage.setItem('afrilink_user', JSON.stringify(res.data));
        onLogin(res.data.token, res.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafaf8] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-black rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-sm font-bold">AL</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">AfriLink Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">{isRegister ? 'Create your developer account' : 'Sign in to continue'}</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
          {isRegister && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Full name</label>
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400" placeholder="Ademoba" />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400" placeholder="you@company.com" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400" placeholder="Min 8 characters" />
          </div>
          {error && <p className="text-red-500 text-xs text-center">{error}</p>}
          <button type="submit" disabled={loading}
            className="w-full py-3 bg-black text-white rounded-xl font-semibold text-sm hover:bg-gray-800 disabled:opacity-60 transition">
            {loading ? 'Please wait...' : isRegister ? 'Create account' : 'Sign in'}
          </button>
          <p className="text-center text-xs text-gray-400">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button type="button" onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-blue-600 hover:text-blue-800 font-medium">{isRegister ? 'Sign in' : 'Register'}</button>
          </p>
        </form>
      </div>
    </div>
  );
};

// ─── Shared UI ─────────────────────────────────────────
const NAV_ITEMS = [
  { id: 'overview', label: 'Overview', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1' },
  { id: 'accounts', label: 'Linked accounts', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
  { id: 'logs', label: 'Consent logs', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
  { id: 'api-keys', label: 'API keys', icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' },
  { id: 'webhooks', label: 'Webhooks', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
];

const NavIcon = ({ path }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d={path} /></svg>
);

const Badge = ({ text, color }) => {
  const s = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    gray: 'bg-gray-100 text-gray-600 border-gray-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
  };
  return <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded-md border ${s[color]}`}>{text}</span>;
};

const actionBadge = (a) => {
  const m = {
    LINK_ACCOUNT: { t: 'LINK ACCOUNT', c: 'blue' },
    CAC_LOOKUP: { t: 'CAC LOOKUP', c: 'amber' },
    CONSENT_REVOKED: { t: 'REVOKED', c: 'red' },
    BVN_VERIFY: { t: 'BVN VERIFY', c: 'purple' },
  };
  const d = m[a] || { t: a, c: 'gray' };
  return <Badge text={d.t} color={d.c} />;
};

const fmtDate = (d) => new Date(d).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', year: 'numeric' });
const fmtTime = (d) => new Date(d).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' });
const fmtMoney = (n, c) => `${c} ${n.toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;

// ─── Main Dashboard ────────────────────────────────────
const AdminDashboard = () => {
  const [installPrompt, setInstallPrompt] = React.useState(null);
  const [notifPermission, setNotifPermission] = React.useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');

  // Capture install prompt
  React.useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') setInstallPrompt(null);
  };

  const enableNotifications = async () => {
    if (!('Notification' in window)) return;
    const perm = await Notification.requestPermission();
    setNotifPermission(perm);
    if (perm === 'granted') {
      new Notification('AfriLink', { body: 'Notifications enabled! You will be notified of new support sessions.', icon: '/icon-192.png' });
    }
  };
  const [token, setToken] = useState(localStorage.getItem('afrilink_token') || '');
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('afrilink_user')); } catch { return null; }
  });
  const [page, setPage] = useState('overview');
  const [stats, setStats] = useState({ totalLinked: 0, todayConsents: 0, todayLinked: 0, totalCACLookups: 0 });
  const [accounts, setAccounts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [apiApps, setApiApps] = useState([]);
  const [isLive, setIsLive] = useState(false);
  const [newAppName, setNewAppName] = useState('');
  const [createdApp, setCreatedApp] = useState(null);

  const handleLogin = (t, u) => { setToken(t); setUser(u); };

  const handleLogout = () => {
    localStorage.removeItem('afrilink_token');
    localStorage.removeItem('afrilink_user');
    setToken('');
    setUser(null);
  };

  const loadLiveData = useCallback(async () => {
    if (!token) return;
    try {
      const [s, a, l] = await Promise.all([
        fetchDashboardStats(token),
        fetchLinkedAccounts(token),
        fetchConsentLogs(token),
      ]);
      setStats(s); setAccounts(a || []); setLogs(l || []); setIsLive(true);
    } catch (err) {
      if (err.response?.status === 403 || err.response?.status === 401) {
        handleLogout();
      }
      setIsLive(false);
    }
  }, [token]);

  const loadApps = useCallback(async () => {
    if (!token) return;
    try {
      const res = await listApiApps(token);
      setApiApps(res.data || []);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { loadLiveData(); loadApps(); }, [loadLiveData, loadApps]);

  const handleCreateApp = async () => {
    if (!newAppName.trim()) return;
    try {
      const res = await createApiApp(token, newAppName.trim());
      setCreatedApp(res.data);
      setNewAppName('');
      loadApps();
    } catch { /* ignore */ }
  };

  // Show login if no token
  if (!token) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div className="min-h-screen bg-[#fafaf8] flex">
      <aside className="w-56 bg-white border-r border-gray-200 flex flex-col flex-shrink-0 sticky top-0 h-screen">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
              <span className="text-white text-[11px] font-bold tracking-tight">AL</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 leading-tight">AfriLink</p>
              <p className="text-[10px] text-gray-400">Dashboard</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {NAV_ITEMS.map((item) => (
            <button key={item.id} onClick={() => setPage(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${
                page === item.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}>
              <NavIcon path={item.icon} />{item.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-emerald-500' : 'bg-amber-400'}`} />
            <span className="text-xs text-gray-500">{isLive ? 'Live data' : 'Connecting...'}</span>
          </div>
          <p className="text-[11px] text-gray-400 mb-2 truncate">{user?.email}</p>
          <button onClick={handleLogout} className="text-[11px] text-red-500 hover:text-red-700">Sign out</button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <header className="sticky top-0 z-30 bg-[#fafaf8]/80 backdrop-blur-sm border-b border-gray-200/60 px-8 py-4 flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-900">{NAV_ITEMS.find((n) => n.id === page)?.label || 'Dashboard'}</h1>
          <button onClick={loadLiveData} className="flex items-center gap-2 px-4 py-2 text-xs font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15"/></svg>
            Refresh
          </button>
        </header>

        <div className="p-8">
          {page === 'overview' && (
            <div>
              <div className="grid grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Linked accounts', value: stats.totalLinked, color: '#1D9E75' },
                  { label: "Today's consents", value: stats.todayConsents, color: '#378ADD' },
                  { label: "Today's links", value: stats.todayLinked, color: '#7F77DD' },
                  { label: 'CAC lookups', value: stats.totalCACLookups, color: '#D85A30' },
                ].map((c) => (
                  <div key={c.label} className="bg-white rounded-2xl border border-gray-200/80 p-5">
                    <p className="text-xs text-gray-500 mb-3">{c.label}</p>
                    <p className="text-3xl font-bold text-gray-900">{c.value.toLocaleString()}</p>
                    <div className="mt-4 h-1 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.min((c.value / Math.max(stats.totalLinked, 1)) * 100, 100)}%`, backgroundColor: c.color }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-900">Recent accounts</h2>
                    <button onClick={() => setPage('accounts')} className="text-xs text-blue-600 hover:text-blue-800">View all</button>
                  </div>
                  {accounts.length === 0 && <p className="px-5 py-8 text-sm text-gray-400 text-center">No linked accounts yet</p>}
                  {accounts.slice(0, 4).map((a) => (
                    <div key={a.id} className="px-5 py-3.5 border-b border-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">{a.institution.slice(0, 2).toUpperCase()}</div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{a.accountName}</p>
                          <p className="text-[11px] text-gray-400">{a.institution}</p>
                        </div>
                      </div>
                      <Badge text={a.status} color={a.status === 'LINKED' ? 'green' : 'red'} />
                    </div>
                  ))}
                </div>
                <div className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-900">Recent activity</h2>
                    <button onClick={() => setPage('logs')} className="text-xs text-blue-600 hover:text-blue-800">View all</button>
                  </div>
                  {logs.length === 0 && <p className="px-5 py-8 text-sm text-gray-400 text-center">No activity yet</p>}
                  {logs.slice(0, 5).map((l) => (
                    <div key={l.id} className="px-5 py-3 border-b border-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-3">{actionBadge(l.action)}<span className="text-xs text-gray-500 font-mono">{l.userId}</span></div>
                      <span className="text-[11px] text-gray-400">{fmtTime(l.createdAt)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {page === 'accounts' && (
            <div className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <p className="text-sm text-gray-500">{accounts.length} accounts</p>
              </div>
              {accounts.length === 0 && <p className="px-6 py-12 text-sm text-gray-400 text-center">No linked accounts yet. Use the Connect Widget to link an account.</p>}
              {accounts.length > 0 && (
                <table className="w-full text-left">
                  <thead><tr className="border-b border-gray-100 bg-gray-50/50">
                    {['Account', 'Institution', 'Balance', 'Status', 'Linked'].map((h) => (
                      <th key={h} className="px-6 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {accounts.map((a) => (
                      <tr key={a.id} className="border-b border-gray-50 hover:bg-blue-50/30 transition">
                        <td className="px-6 py-4"><p className="text-sm font-medium text-gray-900">{a.accountName}</p><p className="text-[11px] text-gray-400 font-mono mt-0.5">{a.userId}</p></td>
                        <td className="px-6 py-4 text-sm text-gray-700">{a.institution}</td>
                        <td className="px-6 py-4 text-sm font-mono text-gray-900">{a.balance != null ? fmtMoney(a.balance, a.currency) : '-'}</td>
                        <td className="px-6 py-4"><Badge text={a.status} color={a.status === 'LINKED' ? 'green' : 'red'} /></td>
                        <td className="px-6 py-4 text-sm text-gray-600">{fmtDate(a.linkedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {page === 'logs' && (
            <div className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden">
              {logs.length === 0 && <p className="px-6 py-12 text-sm text-gray-400 text-center">No consent logs yet.</p>}
              {logs.length > 0 && (
                <table className="w-full text-left">
                  <thead><tr className="border-b border-gray-100 bg-gray-50/50">
                    {['Action', 'User', 'Resource', 'IP', 'Time'].map((h) => (
                      <th key={h} className="px-6 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {logs.map((l) => (
                      <tr key={l.id} className="border-b border-gray-50">
                        <td className="px-6 py-3.5">{actionBadge(l.action)}</td>
                        <td className="px-6 py-3.5 text-sm text-gray-700 font-mono">{l.userId}</td>
                        <td className="px-6 py-3.5 text-sm text-gray-500 font-mono">{l.resourceId || '-'}</td>
                        <td className="px-6 py-3.5 text-sm text-gray-400">{l.ipAddress || '-'}</td>
                        <td className="px-6 py-3.5 text-sm text-gray-600">{fmtDate(l.createdAt)} {fmtTime(l.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {page === 'api-keys' && (
            <div>
              <div className="bg-white rounded-2xl border border-gray-200/80 p-6 mb-6">
                <h2 className="text-sm font-semibold text-gray-900 mb-4">Create new app</h2>
                <div className="flex gap-3">
                  <input type="text" value={newAppName} onChange={(e) => setNewAppName(e.target.value)}
                    placeholder="App name (e.g. My Fintech App)" className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-gray-400" />
                  <button onClick={handleCreateApp} className="px-6 py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-gray-800 transition">Create</button>
                </div>
                {createdApp && (
                  <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <p className="text-sm font-semibold text-emerald-800 mb-2">App created — save your secret key now!</p>
                    <div className="space-y-2 text-xs font-mono">
                      <p><span className="text-gray-500">Public key:</span> <span className="text-gray-900">{createdApp.publicKey}</span> <button onClick={() => {navigator.clipboard.writeText(createdApp.publicKey); alert("Public key copied!")}} style={{marginLeft:"8px",padding:"2px 8px",fontSize:"11px",background:"#f3f4f6",border:"1px solid #e5e7eb",borderRadius:"6px",cursor:"pointer"}}>Copy</button></p>
                      <p><span className="text-gray-500">Secret key:</span> <span className="text-red-700">{createdApp.secretKey}</span></p>
                    </div>
                    <p className="text-[11px] text-emerald-600 mt-2">The secret key will not be shown again.</p>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                {apiApps.map((app) => (
                  <div key={app.id} className="bg-white rounded-2xl border border-gray-200/80 p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-base font-semibold text-gray-900">{app.name}</h3>
                      <Badge text={app.environment.toUpperCase()} color={app.environment === 'live' ? 'green' : 'amber'} />
                      <Badge text={app.status} color={app.status === 'ACTIVE' ? 'green' : 'red'} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-xl p-3.5">
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5">Public key</p>
                        <div className="flex items-center gap-2"><p className="text-sm font-mono text-gray-700 break-all flex-1">{app.publicKey}</p><button onClick={(e) => {navigator.clipboard.writeText(app.publicKey); e.target.textContent = '✓ Copied'; setTimeout(() => e.target.textContent = 'Copy', 1500)}} className="flex-shrink-0 px-2 py-1 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition cursor-pointer">Copy</button></div>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3.5">
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5">Created</p>
                        <p className="text-sm text-gray-700">{fmtDate(app.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {apiApps.length === 0 && <p className="text-sm text-gray-400 text-center py-8">No API apps yet. Create one above.</p>}
              </div>
            </div>
          )}

          {page === 'webhooks' && (
            <div className="text-center py-12">
              <p className="text-sm text-gray-400">Webhook management coming soon.</p>
              <p className="text-xs text-gray-300 mt-2">Use the API to create webhooks: POST /api/webhooks</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
